import {DatePicker, Input} from 'antd'
import {timetable_getsByClassesOwner_Api, timetable_getsByOrganization_Api,} from 'apis'
import classNames from 'classnames'
import getTitle from 'helpers/getTitle'
import {formatSimplifyDate} from 'helpers/dateUtils'
import _ from 'lodash'
import useTranslate from 'modules/local/useTranslate'
import React, {useContext, useMemo} from 'react'
import {BsBuilding, BsCalendar2, BsCalendar2WeekFill, BsChevronDown, BsGeoFill} from 'react-icons/bs'
import {IoCalendarOutline} from 'react-icons/io5'
import {MdMenuBook} from 'react-icons/md'
import {PiMapPinFill} from 'react-icons/pi'
import Toggle from 'components/Toggle'
import usePaginationHelper, {createPagerKey} from 'views/AppLocals/hooks/usePaginationHelper'
import {bindQueryParam, bindQueryParams} from 'views/AppLocals/functions/routerHelper'
import {NullAsDefault, renderIf} from 'views/Shared'
import WorkspaceContext from '../WorkspaceContext'
import {useLocation} from 'react-use'
import {useRouteSearchParam} from 'envs/_current/history'
import {useMenuHandler} from 'views/Shared/handlers/useMenuHandler'
import {Queries} from 'views/Shared/enums/Queries'
import dayjs from 'dayjs'
import Translate from 'modules/local/Translate'
import {GoDotFill} from "react-icons/go";

const orgFilterActions = {
    sortBy: 'org-sort-by',
    keyword: 'org-keyword',
    from_time_open: 'org-from-date',
    to_time_open: 'org-to-date',
}

const courseFilterActions = {
    sortBy: 'cls-sort-by',
    keyword: 'cls-keyword',
    from_time_open: 'cls-from-date',
    to_time_open: 'cls-to-date',
}

const getTimetableDisplayStatus = ({from_date, to_date}) => {
    const now = dayjs()
    const end = to_date ? dayjs(to_date) : dayjs(from_date)
    const start = dayjs(from_date)
    if (end.isBefore(now, 'day')) return 'finished'
    if (start.isAfter(now, 'day')) return 'upcoming'
    return 'ongoing'
}

const TimetableStatusBadge = ({from_date, to_date}) => {
    const t = useTranslate()
    const displayStatus = getTimetableDisplayStatus({from_date, to_date})

    if (displayStatus === 'upcoming') {
        return (
            <span
                style={{padding: '2px 8px'}}
                className="font-bold text-2xs text-blue-500 tracking-widest bg-blue-50 uppercase border border-blue-400 rounded-md">
                {t('upcoming')}
            </span>
        )
    }
    if (displayStatus === 'ongoing') {
        return (
            <span
                style={{padding: '2px 8px'}}
                className="font-bold text-2xs text-green-600 tracking-widest bg-green-50 uppercase border border-green-400 rounded-md">
                {t('active')}
            </span>
        )
    }
    return (
        <span
            style={{padding: '2px 8px'}}
            className="font-bold text-2xs text-gray-500 tracking-widest bg-slate-100 uppercase rounded-md">
            {t('expired')}
        </span>
    )
}

const TimetableFilter = ({
                             searchPlaceholder = 'search',
                             value: filter = {},
                             onChange: setFilter = NullAsDefault,
                         }) => {
    const t = useTranslate()

    return (
        <div className="p-3 border border-color-50 background shadow-sm rounded-xl">
            <div className="flex flex-col md:flex-row gap-3">
                <div className="flex-1 flex flex-col gap-1">
                    <span className="font-medium text-color-300 tracking-wider">{t(searchPlaceholder)}</span>
                    <Input.Search
                        key={filter.keyword || 'empty'}
                        allowClear
                        defaultValue={filter.keyword || ''}
                        placeholder={t('type keyword...')}
                        onSearch={(val) => setFilter({...filter, keyword: val || undefined})}
                    />
                </div>
                <div className="flex flex-col gap-1">
                    <span className="font-medium text-color-300 tracking-wider">{t('from date')}</span>
                    <DatePicker
                        allowClear
                        format="DD/MM/YYYY"
                        placeholder={t('from date')}
                        suffixIcon={<IoCalendarOutline className="text-color-500"/>}
                        value={filter.from_time_open ? dayjs(filter.from_time_open) : undefined}
                        onChange={(_, dateString) => setFilter({...filter, from_time_open: dateString || undefined})}
                    />
                </div>
                <div className="flex flex-col gap-1">
                    <span className="font-medium text-color-300 tracking-wider">{t('to date')}</span>
                    <DatePicker
                        allowClear
                        format="DD/MM/YYYY"
                        placeholder={t('to date')}
                        suffixIcon={<IoCalendarOutline className="text-color-500"/>}
                        value={filter.to_time_open ? dayjs(filter.to_time_open) : undefined}
                        onChange={(_, dateString) => setFilter({...filter, to_time_open: dateString || undefined})}
                    />
                </div>
            </div>
        </div>
    )
}

const OrgTimetableFilter = (props) => (
    <TimetableFilter
        searchPlaceholder="search organization"
        {...props}
    />
)

const CourseTimetableFilter = (props) => (
    <TimetableFilter
        searchPlaceholder="search course class"
        {...props}
    />
)

const TimetableExpandedDetail = ({location_address, notes, timetable_details = []}) => {
    const sortedDetails = useMemo(
        () =>
            [...timetable_details].sort((a, b) => {
                if (!a.start_time) return 1
                if (!b.start_time) return -1
                return dayjs(a.start_time).valueOf() - dayjs(b.start_time).valueOf()
            }),
        [timetable_details]
    )

    return (
        <Translate>
            {(t) => (
                <div className="px-4 py-3 border-t border-color-50 background flex flex-col gap-2">
                    {renderIf(
                        location_address,
                        <div className="flex items-center gap-1.5">
                            <PiMapPinFill
                                size={13}
                                className="text-blue-400 flex-shrink-0"
                            />
                            <span className="font-bold text-sm text-blue-600 tracking-wide">
                                {location_address}
                            </span>
                        </div>
                    )}
                    {renderIf(
                        notes,
                        <div className="p-2 background-100 rounded-lg text-xs italic tracking-wider">
                            <span className="font-medium text-color-100">
                                {`* ${t('note')}: `}
                            </span>
                            <span className="text-color-300 whitespace-pre-line leading-relaxed">
                                {notes}
                            </span>
                        </div>
                    )}
                    {sortedDetails.length > 0 && (
                        <div className="flex flex-col gap-3">
                            {sortedDetails.map((detail, i) => {
                                const prev = i > 0 ? sortedDetails[i - 1] : null
                                const showDate = detail.start_time && (!prev?.start_time || !dayjs(detail.start_time).isSame(dayjs(prev.start_time), 'day'))

                                return (
                                    <div key={i} className="ml-4 flex flex-col gap-1">
                                        {showDate && (
                                            <div className="flex items-center gap-1.5">
                                                <GoDotFill size={14} className="text-slate-400"/>
                                                <span className="font-semibold text-slate-700 tracking-wide">
                                                    {formatSimplifyDate(detail.start_time, 'medium')}
                                                </span>
                                            </div>
                                        )}
                                        <div className="ml-1.5 pl-3 border-l border-color-100 flex flex-col gap-0.5 flex-1 min-w-0">
                                            {detail.start_time && (
                                                <span className="font-mono text-xs font-medium text-secondary-600">
                                                    {dayjs(detail.start_time).format('HH:mm')}
                                                    {detail.end_time
                                                        ? ` – ${dayjs(detail.end_time).format('HH:mm')}`
                                                        : ''
                                                    }
                                                </span>
                                            )}
                                            {renderIf(
                                                detail.location_address && detail.location_address !== location_address,
                                                <span className="flex items-center gap-1 text-xs text-gray-600 tracking-wider">
                                                    <BsGeoFill size={10} className="flex-shrink-0"/>
                                                    {detail.location_address}
                                                </span>
                                            )}
                                            {renderIf(
                                                detail.notes,
                                                <div className="mt-0.5 p-2 background-100 rounded-lg text-xs italic tracking-wider">
                                                    <span className="font-medium text-color-200">
                                                        {`* ${t('note')}: `}
                                                    </span>
                                                    <span className="text-color-300 whitespace-pre-line leading-relaxed">
                                                        {detail.notes}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    )}
                </div>
            )}
        </Translate>
    )
}

const renderOrgTimetableItem = (item, index) => {
    const {title, group, from_date, to_date, location_address, timetable_details = [], notes} = item ?? {}

    const one_day = !to_date || dayjs(from_date).format('YYYY-MM-DD') === dayjs(to_date).format('YYYY-MM-DD')
    const isExpired = getTimetableDisplayStatus({from_date, to_date}) === 'finished'

    return (
        <Toggle key={index}>
            {(isOpen, toggle) => (
                <div className="transition-all duration-300 relative list-item">
                    <div
                        onClick={(e) => {
                            e.preventDefault()
                            toggle()
                        }}
                        className="p-4 flex items-start gap-3 cursor-pointer select-none">
                        <div
                            className={classNames(
                                'w-10 h-10 rounded-lg flex flex-center flex-shrink-0',
                                isExpired ? 'background-200' : 'bg-secondary-50'
                            )}>
                            <BsCalendar2WeekFill size={18} className={isExpired ? 'text-color-500' : 'text-secondary-500'}/>
                        </div>
                        <div className="flex flex-col flex-1 min-w-0 gap-1">
                            <span className={classNames(
                                'text-base leading-tight',
                                isExpired ? 'font-semibold text-color-300 line-through' : isOpen ? 'font-bold text-secondary-600' : 'font-bold text-gray-800'
                            )}>
                                {title}
                            </span>
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                                <span className="flex items-center gap-1 font-medium text-xs text-color-300 tracking-wider">
                                    <BsCalendar2 size={11} className="text-color-500 flex-shrink-0"/>
                                    {one_day
                                        ? formatSimplifyDate(from_date)
                                        : `${formatSimplifyDate(from_date)} - ${formatSimplifyDate(to_date)}`
                                    }
                                </span>
                                {group?.title && (
                                    <span className="flex items-center gap-1 font-medium text-xs text-color-300 tracking-wider">
                                        <BsBuilding size={11} className="text-color-500 flex-shrink-0"/>
                                        {group.title}
                                    </span>
                                )}
                                <div className="flex-1"/>
                                <TimetableStatusBadge from_date={from_date} to_date={to_date}/>
                            </div>
                        </div>
                        <div className="text-color-300 flex-shrink-0 mt-1">
                            <BsChevronDown size={14} style={{transform: isOpen ? 'rotate(-90deg)' : ''}}/>
                        </div>
                    </div>
                    {isOpen && (
                        <TimetableExpandedDetail
                            location_address={location_address}
                            notes={notes}
                            timetable_details={timetable_details}
                        />
                    )}
                </div>
            )}
        </Toggle>
    )
}

const renderCourseTimetableItem = (item, index) => {
    const {course_class, subject, from_date, to_date, location_address, timetable_details = [], notes} = item ?? {}

    const one_day = !to_date || dayjs(from_date).format('YYYY-MM-DD') === dayjs(to_date).format('YYYY-MM-DD')
    const isExpired = getTimetableDisplayStatus({from_date, to_date}) === 'finished'
    const classTitle = getTitle(course_class)
    const subjectTitle = getTitle(subject)

    return (
        <Toggle key={index}>
            {(isOpen, toggle) => (
                <div className="transition-all duration-300 relative list-item">
                    <div
                        onClick={(e) => {
                            e.preventDefault()
                            toggle()
                        }}
                        className="p-4 flex items-start gap-3 cursor-pointer select-none">
                        <div
                            className={classNames(
                                'w-10 h-10 rounded-lg flex flex-center flex-shrink-0',
                                isExpired ? 'background-200' : 'bg-secondary-50'
                            )}>
                            <MdMenuBook size={18} className={isExpired ? 'text-color-500' : 'text-secondary-500'}/>
                        </div>
                        <div className="flex flex-col flex-1 min-w-0 gap-1">
                            <span className={classNames(
                                'text-base leading-tight',
                                isExpired ? 'font-semibold text-color-300 line-through' : isOpen ? 'font-bold text-secondary-600' : 'font-bold text-gray-800'
                            )}>
                                {subjectTitle || classTitle}
                            </span>
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                                <span className="flex items-center gap-1 font-medium text-xs text-color-300 tracking-wider">
                                    <BsCalendar2 size={11} className="text-color-500 flex-shrink-0"/>
                                    {one_day
                                        ? formatSimplifyDate(from_date)
                                        : `${formatSimplifyDate(from_date)} - ${formatSimplifyDate(to_date)}`
                                    }
                                </span>
                                {classTitle && (
                                    <span className="flex items-center gap-1 font-medium text-xs text-color-300 tracking-wider">
                                        <MdMenuBook size={11} className="text-color-500 flex-shrink-0"/>
                                        {classTitle}
                                    </span>
                                )}
                                <div className="flex-1"/>
                                <TimetableStatusBadge from_date={from_date} to_date={to_date}/>
                            </div>
                        </div>
                        <div className="text-color-300 flex-shrink-0 mt-1">
                            <BsChevronDown size={14} style={{transform: isOpen ? 'rotate(-90deg)' : ''}}/>
                        </div>
                    </div>
                    {isOpen && (
                        <TimetableExpandedDetail
                            location_address={location_address}
                            notes={notes}
                            timetable_details={timetable_details}
                        />
                    )}
                </div>
            )}
        </Toggle>
    )
}

const WorkspaceOrgTimetableList = ({organization_id}) => {
    const location = useLocation()

    const keyword = useRouteSearchParam(orgFilterActions.keyword)
    const from_time_open = useRouteSearchParam(orgFilterActions.from_time_open)
    const to_time_open = useRouteSearchParam(orgFilterActions.to_time_open)

    const refresh_deps = useMemo(
        () => [
            organization_id,
            keyword,
            from_time_open,
            to_time_open,
            `refreshToken=${_.get(location.state, 'refreshToken')}`,
        ],
        [organization_id, keyword, from_time_open, to_time_open, location.state]
    )

    const [pager_widget] = usePaginationHelper({
        pagerInfo: {
            RenderEntity: React.Fragment,
            PaginationMeta: NullAsDefault,
            apiInfo: timetable_getsByOrganization_Api,
            query: bindQueryParam({id: organization_id}),
            renderItem: renderOrgTimetableItem,
            renderWrap: (child) => (
                <div className="mt-3 flex flex-col divide-y divide-gray-200 list-item-workspace">
                    {child}
                </div>
            ),
        },
        SortByWidget: NullAsDefault,
        renderSortBy: NullAsDefault,
        isToggleDefault: true,
        query_params: orgFilterActions,
        onInit: ({onFilterChange = NullAsDefault}) => {
            onFilterChange({keyword, from_time_open, to_time_open})
        },
        filter_params: {keyword, from_time_open, to_time_open},
        dependencies: [organization_id, keyword, from_time_open, to_time_open],
        withKey: createPagerKey(refresh_deps),
        AdvancedFilter: OrgTimetableFilter,
        renderFilter: (_, filter_widget) => filter_widget,
        renderHeader: ([_, filterBar]) => filterBar,
    })

    return (
        <div className="flex flex-col">
            {pager_widget}
            <div className="h-16"/>
        </div>
    )
}

const WorkspaceCourseTimetableList = ({organization_id}) => {
    const location = useLocation()

    const keyword = useRouteSearchParam(courseFilterActions.keyword)
    const from_time_open = useRouteSearchParam(courseFilterActions.from_time_open)
    const to_time_open = useRouteSearchParam(courseFilterActions.to_time_open)

    const refresh_deps = useMemo(
        () => [
            organization_id,
            keyword,
            from_time_open,
            to_time_open,
            `refreshToken=${_.get(location.state, 'refreshToken')}`,
        ],
        [organization_id, keyword, from_time_open, to_time_open, location.state]
    )

    const [pager_widget] = usePaginationHelper({
        pagerInfo: {
            RenderEntity: React.Fragment,
            PaginationMeta: NullAsDefault,
            apiInfo: timetable_getsByClassesOwner_Api,
            query: bindQueryParams([{id: organization_id}, {type: 'organization'}]),
            renderItem: renderCourseTimetableItem,
            renderWrap: (child) => (
                <div className="mt-3 flex flex-col divide-y divide-gray-200 list-item-workspace">
                    {child}
                </div>
            ),
        },
        SortByWidget: NullAsDefault,
        renderSortBy: NullAsDefault,
        isToggleDefault: true,
        query_params: courseFilterActions,
        onInit: ({onFilterChange = NullAsDefault}) => {
            onFilterChange({keyword, from_time_open, to_time_open})
        },
        filter_params: {keyword, from_time_open, to_time_open},
        dependencies: [organization_id, keyword, from_time_open, to_time_open],
        withKey: createPagerKey(refresh_deps),
        AdvancedFilter: CourseTimetableFilter,
        renderFilter: (_, filter_widget) => filter_widget,
        renderHeader: ([_, filterBar]) => filterBar,
    })

    return (
        <div className="flex flex-col">
            {pager_widget}
            <div className="h-16"/>
        </div>
    )
}

const TabKeys = Object.freeze({
    ORGANIZATION: 'org-timetable',
    COURSE: 'course-timetable',
})

const tabRoutes = [
    {key: TabKeys.ORGANIZATION, path: TabKeys.ORGANIZATION},
    {key: TabKeys.COURSE, path: TabKeys.COURSE},
]

const WorkspaceActivityCalendar = () => {
    const t = useTranslate()
    const {organization_id} = useContext(WorkspaceContext)

    const {activeKey, onMenuSelect} = useMenuHandler({
        routes: tabRoutes,
        actionName: Queries.TAB,
        defaultKey: TabKeys.ORGANIZATION,
    })

    const tabs = [
        {key: TabKeys.ORGANIZATION, label: 'organization'},
        {key: TabKeys.COURSE, label: 'class timetable'},
    ]

    return (
        <div className="flex flex-col gap-4">
            <div className="flex flex-col md:flex-row md:items-center gap-3">
                <div className="flex flex-col flex-1">
                    <h2 className="font-bold text-xl text-gray-800 tracking-wide">
                        {t('activity schedule')}
                    </h2>
                    <div className="mt-1 w-20 h-1 rounded-full bg-gray-400"/>
                </div>
            </div>
            <div className="flex gap-3">
                {tabs.map(({key, label}) => (
                    <button
                        key={key}
                        onClick={() => onMenuSelect(key)}
                        className={classNames(
                            'flex items-center gap-1.5 px-3 py-1.5 shadow rounded-lg text-xs font-medium transition-all duration-300',
                            activeKey === key
                                ? 'bg-secondary-500 text-white'
                                : 'background text-color-200 hover:bg-secondary-50 hover:text-secondary-500'
                        )}>
                        {t(label)}
                    </button>
                ))}
            </div>
            {activeKey === TabKeys.ORGANIZATION
                ? <WorkspaceOrgTimetableList organization_id={organization_id}/>
                : <WorkspaceCourseTimetableList organization_id={organization_id}/>
            }
        </div>
    )
}

export default WorkspaceActivityCalendar
